import React, { useState, useEffect } from 'react';
import { Modal, Table, DatePicker, Select, Space, Tag, Spin, Alert, Typography } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

/**
 * Модальное окно для просмотра истории изменений фактов менеджера
 * @param {Object} props
 * @param {Object} props.manager - данные менеджера { id, name }
 * @param {Function} props.onClose - callback при закрытии
 * @param {boolean} props.visible - видимость модального окна
 * @param {Function} props.fetchHistory - функция загрузки истории (managerId, from, to)
 * @param {string} props.currency - валюта для отображения
 */
const FactsHistoryModal = ({ manager, onClose, visible, fetchHistory, currency = 'KZT' }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [dateRange, setDateRange] = useState([moment().subtract(30, 'days'), moment()]);
  const [metricFilter, setMetricFilter] = useState('all');

  useEffect(() => {
    if (visible && manager?.id) {
      loadHistory();
    }
  }, [visible, manager, dateRange]);

  const loadHistory = async () => {
    if (!manager?.id || !fetchHistory) return;

    try {
      setLoading(true);
      const from = dateRange[0].format('YYYY-MM-DD');
      const to = dateRange[1].format('YYYY-MM-DD');
      const data = await fetchHistory(manager.id, from, to);
      setHistory(data || []);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    try {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: currency || 'KZT',
        maximumFractionDigits: 0,
      }).format(value || 0);
    } catch (e) {
      return `${value || 0} ${currency || '₸'}`;
    }
  };

  const formatNumber = (value) => {
    try {
      return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Number(value || 0));
    } catch (e) {
      return String(value || 0);
    }
  };

  const getMetricLabel = (metric) => {
    const labels = {
      leads: 'Лиды',
      qualified: 'Квалифицированные лиды',
      measure: 'Замеры',
      salesCount: 'Количество продаж',
      salesSum: 'Сумма продаж',
      avgCheck: 'Средний чек',
    };
    return labels[metric] || metric;
  };

  const getMetricColor = (metric) => {
    const colors = {
      leads: 'blue',
      qualified: 'cyan',
      measure: 'green',
      salesCount: 'orange',
      salesSum: 'red',
      avgCheck: 'purple',
    };
    return colors[metric] || 'default';
  };

  const formatValue = (metric, value) => {
    if (value === null || value === undefined) return '—';
    
    const moneyMetrics = ['salesSum', 'avgCheck'];
    if (moneyMetrics.includes(metric)) {
      return formatCurrency(value);
    }
    return formatNumber(value);
  };

  const columns = [
    {
      title: 'Дата изменения',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => moment(date).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Дата факта',
      dataIndex: 'factDate',
      key: 'factDate',
      width: 120,
      render: (date) => moment(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Показатель',
      dataIndex: 'metric',
      key: 'metric',
      width: 180,
      render: (metric) => (
        <Tag color={getMetricColor(metric)}>{getMetricLabel(metric)}</Tag>
      ),
      filters: [
        { text: 'Лиды', value: 'leads' },
        { text: 'Квалифицированные лиды', value: 'qualified' },
        { text: 'Замеры', value: 'measure' },
        { text: 'Количество продаж', value: 'salesCount' },
        { text: 'Сумма продаж', value: 'salesSum' },
        { text: 'Средний чек', value: 'avgCheck' },
      ],
      onFilter: (value, record) => record.metric === value,
    },
    {
      title: 'Старое значение',
      dataIndex: 'oldValue',
      key: 'oldValue',
      width: 140,
      render: (value, record) => (
        <Text type="secondary">{formatValue(record.metric, value)}</Text>
      ),
    },
    {
      title: 'Новое значение',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 140,
      render: (value, record) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatValue(record.metric, value)}
        </Text>
      ),
    },
    {
      title: 'Администратор',
      dataIndex: 'editedBy',
      key: 'editedBy',
      width: 150,
      render: (user) => user?.name || user?.email || 'Неизвестно',
    },
  ];

  // Фильтрация по выбранному показателю
  const filteredHistory = metricFilter === 'all' 
    ? history 
    : history.filter(item => item.metric === metricFilter);

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined />
          {`История изменений: ${manager?.name || 'Менеджер'}`}
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose={true}
      maskClosable={false}
      style={{ zIndex: 2000 }}
      getContainer={() => document.body}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="DD.MM.YYYY"
            style={{ width: 240 }}
          />
          <Select
            value={metricFilter}
            onChange={setMetricFilter}
            style={{ width: 200 }}
            placeholder="Фильтр по показателю"
          >
            <Option value="all">Все показатели</Option>
            <Option value="leads">Лиды</Option>
            <Option value="qualified">Квалифицированные лиды</Option>
            <Option value="measure">Замеры</Option>
            <Option value="salesCount">Количество продаж</Option>
            <Option value="salesSum">Сумма продаж</Option>
            <Option value="avgCheck">Средний чек</Option>
          </Select>
        </Space>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" tip="Загрузка истории..." />
          </div>
        ) : filteredHistory.length > 0 ? (
          <Table
            columns={columns}
            dataSource={filteredHistory}
            rowKey={(record) => `${record.id || record._id || Math.random()}`}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Всего записей: ${total}`,
            }}
            size="small"
            bordered
          />
        ) : (
          <Alert
            message="История изменений пуста"
            description="За выбранный период изменений фактов не найдено"
            type="info"
            showIcon
          />
        )}
      </Space>
    </Modal>
  );
};

export default FactsHistoryModal;

